import { DataFormResponse } from './types'
import type { FormSubmitResponse } from '../FormComponent/types'

/**
 * Populates default values in the schema properties from the form data
 * @param formDataResponse - The DataFormResponse object containing uiSchema and dataSchema
 * @returns A new DataFormResponse with updated default values in schema properties
 */
export function populateDefaultFormValues(formDataResponse: DataFormResponse): DataFormResponse {
  // Check if formData is of type FormData (not an empty record)
  const formData = formDataResponse.dataSchema.formData

  // If formData is empty or not a FormData type, return the original response
  if (!formData || typeof formData !== 'object' || Object.keys(formData).length === 0) {
    return formDataResponse
  }

  // Create a deep copy of the response to avoid mutating the original
  const updatedResponse = {
    ...formDataResponse,
    uiSchema: {
      ...formDataResponse.uiSchema,
      schema: {
        ...formDataResponse.uiSchema.schema,
        properties: formDataResponse.uiSchema.schema.properties.map((property) => {
          // Check if there's a corresponding value in formData for this property
          const propertyId = property.id

          if (propertyId in formData) {
            // Add or replace the default value with the value from formData
            return {
              ...property,
              default: formData[propertyId],
            }
          }

          // Return the property unchanged if no corresponding formData value
          return property
        }),
      },
    },
  }

  return updatedResponse
}

export async function synchronizeDataFormAfterSubmit<TRefreshedData>(
  submitForm: () => Promise<FormSubmitResponse>,
  refetch: () => Promise<TRefreshedData>
): Promise<TRefreshedData | undefined> {
  const response = await submitForm()
  if (response.uiSchema.success === false) {
    return undefined
  }
  return refetch()
}
